import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ParentProductIdContext } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledLink from "@tSurya/theme/components/StyledLink";
import { ELEMENTS } from "@tSurya/theme/design";
import GVLeftArrow from "@tSurya/theme/styleguide/icons/GVLeftArrow";
import GVRightArrow from "@tSurya/theme/styleguide/icons/GVRightArrow";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import SurProductListProductGridCrad from "@tSurya/Widgets/Product/SurProductListProductGridCrad";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    widgetPosition = "widgetPosition",
    linkText = "linkText",
    linkUrl = "linkUrl",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.widgetPosition]: number;
        [fields.linkText]: string;
        [fields.linkUrl]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const productsData = getProductsForProductInfoList(state, ownProps.id);
    return {
        products: productsData?.filter(o => !!o),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        session: state.context.session,
        productRecommendationWidgets: state.components.productRecommendations.widgets,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface RelatedProductsGridStyles {
    outerWrapper?: InjectableCss;
    innerContainer?: InjectableCss;
    wrapper?: InjectableCss;
    titleContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    productGridItem?: GridItemProps;
    productWrapper?: InjectableCss;
    productItemWrapper?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    linkContainer?: InjectableCss;

    carouselMainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;

    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
}

export const relatedProductsGridStyles: RelatedProductsGridStyles = {
    outerWrapper: {
        css: css`
            background-color: #FAF8F5;
            padding: 40px 0;
            margin-bottom: 38px;
            margin-top: 38px;
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 40px;
                        margin-top: 40px;
                    `,
                    css`
                        margin-bottom: 40px;
                        margin-top: 40px;
                    `,
                    css`
                        margin-bottom: 40px;
                        margin-top: 40px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    innerContainer: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;
            padding: 0 40px;
        `,
    },
    wrapper: {
        css: css``,
    },
    titleContainer: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 24px;
                    `,
                    css`
                        margin-bottom: 24px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            color: #262525;
            font-weight: 100;
            font-size: 40px;
            line-height: 48px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-weight: 100;
                            font-size: 24px;
                            line-height: 32px;
                        `,
                        css`
                            font-weight: 100;
                            font-size: 24px;
                            line-height: 32px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    gridContainer: {
        gap: 20,
        css: css`
            max-width: 1440px;
            margin: 0 auto;
        `,
    },
    productGridItem: {
        css: css`
        `,
    },
    productWrapper: {
        css: css`
            width: 100%;
        `,
    },
    productItemWrapper: {
        css: css`
            position: relative;
            width: 100%;
            height: 100%;
            
            [data-test-selector*="addToList"] {
                display: none !important;
            }
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    linkContainer: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 20px;
        `,
    },

    carouselMainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: none;
            padding: 0;
            background-color: transparent;
            width: ${ELEMENTS.carouselArrows.size};
            height: ${ELEMENTS.carouselArrows.size};
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
            flex-shrink: 0;
            min-width: ${ELEMENTS.carouselArrows.size};
            min-height: ${ELEMENTS.carouselArrows.size};
            padding: ${ELEMENTS.carouselArrows.padding};
            border-radius: 50%;

            > span {
                top: ${ELEMENTS.carouselArrows.iconTop} !important;
            }

            svg [stroke]:not([stroke='none']) {
                stroke: currentColor !important;
            }

            svg [fill]:not([fill='none']) {
                fill: currentColor !important;
            }

            &:hover {
                background: transparent;
                border: none;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: none;
            padding: 0;
            background-color: transparent;
            width: ${ELEMENTS.carouselArrows.size};
            height: ${ELEMENTS.carouselArrows.size};
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
            flex-shrink: 0;
            min-width: ${ELEMENTS.carouselArrows.size};
            min-height: ${ELEMENTS.carouselArrows.size};
            padding: ${ELEMENTS.carouselArrows.padding};
            border-radius: 50%;

            > span {
                top: ${ELEMENTS.carouselArrows.iconTop} !important;
            }

            svg [stroke]:not([stroke='none']) {
                stroke: currentColor !important;
            }

            svg [fill]:not([fill='none']) {
                fill: currentColor !important;
            }

            &:hover {
                background: transparent;
                border: none;
            }

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 10px;
            display: flex;
            flex-direction: column;
        `,
    },
};

const getThemeAwareStyles = (isGlobalViews: boolean, productCount: number): RelatedProductsGridStyles => {
    const baseStyles = relatedProductsGridStyles;

    const desktopItemWidth = "33.3333%";
    const shouldCenterOne = productCount === 1;
    const shouldCenterTwo = productCount === 2;
    
    const dynamicStyles: RelatedProductsGridStyles = {
        ...baseStyles,
        gridContainer: {
            ...baseStyles.gridContainer,
            css: css`
                ${baseStyles.gridContainer?.css}
            `,
            offsetCss:
                shouldCenterOne || shouldCenterTwo
                    ? css`
                          justify-content: center;
                          ${shouldCenterOne
                              ? css`
                                    > * {
                                        margin-left: auto;
                                        margin-right: auto;
                                    }
                                `
                              : css``}
                          ${shouldCenterTwo
                              ? css`
                                    > *:first-child {
                                        margin-left: auto;
                                    }
                                    > *:last-child {
                                        margin-right: auto;
                                    }
                                `
                              : css``}
                      `
                    : undefined,
        },
        productGridItem: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: 100%;
                            max-width: 100%;
                        `,
                        css`
                            flex-basis: 50%;
                            max-width: 50%;
                        `,
                        css`
                            flex-basis: ${desktopItemWidth};
                            max-width: ${desktopItemWidth};
                        `,
                        css`
                            flex-basis: ${desktopItemWidth};
                            max-width: ${desktopItemWidth};
                        `,
                        css`
                            flex-basis: ${desktopItemWidth};
                            max-width: ${desktopItemWidth};
                        `,
                    ])}
            `,
        },
    };
    
    if (isGlobalViews) {
        return {
            ...dynamicStyles,
            titleText: {
                ...baseStyles.titleText,
                css: css`
                    ${baseStyles.titleText?.css}
                    font-family: 'Fraunces-72pt', serif;
                    font-style: italic;
                    font-weight: 100;
                `,
            },
            carouselSlide: {
                ...baseStyles.carouselSlide,
                css: css`
                    position: relative;
                    flex: 0 0 auto;
                    padding: 0 10px;

                    @media (max-width: 767px) {
                        padding: 0;
                    }
                `,
            },
            carouselWrapper: {
                ...baseStyles.carouselWrapper,
                css: css`
                    width: 100%;
                    position: relative;
                    overflow: visible;

                    @media (max-width: 767px) {
                        overflow: hidden;
                    }
                `,
            },
            carouselContainer: {
                ...baseStyles.carouselContainer,
                css: css`
                    overflow: hidden;
                    width: 100%;

                    @media (max-width: 767px) {
                        display: flex;
                        padding: 0 20px;
                    }
                `,
            },
            carouselSlidesContainer: {
                ...baseStyles.carouselSlidesContainer,
                css: css`
                    display: flex;
                    flex-wrap: nowrap;
                    justify-content: flex-start;
                    width: 100%;

                    > * {
                        margin-right: 20px;

                        @media (max-width: 767px) {
                            margin-right: 16px;
                        }
                    }
                `,
            },
            prevArrowWrapper: {
                css: css`
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 10;
                `,
            },
            nextArrowWrapper: {
                css: css`
                    position: absolute;
                    right: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 10;
                `,
            },
            prevArrowButton: {
                ...baseStyles.prevArrowButton,
                css: css`
                    background-color: #FFFFFF;
                    border: ${ELEMENTS.carouselArrows.border};
                    width: ${ELEMENTS.carouselArrows.size};
                    height: ${ELEMENTS.carouselArrows.size};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
                    flex-shrink: 0;
                    min-width: ${ELEMENTS.carouselArrows.size};
                    min-height: ${ELEMENTS.carouselArrows.size};
                    padding: ${ELEMENTS.carouselArrows.padding};

                    > span {
                        top: ${ELEMENTS.carouselArrows.iconTop} !important;
                    }

                    &:hover {
                        background: #FFFFFF;
                        border: ${ELEMENTS.carouselArrows.border};
                    }

                    &:disabled {
                        display: none;
                    }
                `,
            },
            nextArrowButton: {
                ...baseStyles.nextArrowButton,
                css: css`
                    background-color: #FFFFFF;
                    border: ${ELEMENTS.carouselArrows.border};
                    width: ${ELEMENTS.carouselArrows.size};
                    height: ${ELEMENTS.carouselArrows.size};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: ${ELEMENTS.carouselArrows.boxShadow};
                    flex-shrink: 0;
                    min-width: ${ELEMENTS.carouselArrows.size};
                    min-height: ${ELEMENTS.carouselArrows.size};
                    padding: ${ELEMENTS.carouselArrows.padding};

                    > span {
                        top: ${ELEMENTS.carouselArrows.iconTop} !important;
                    }

                    &:hover {
                        background: #FFFFFF;
                        border: ${ELEMENTS.carouselArrows.border};
                    }

                    &:disabled {
                        display: none;
                    }
                `,
            },
        };
    }
    
    return dynamicStyles;
};

const RelatedProductsGrid: React.FC<Props> = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    session,
    productRecommendationWidgets,
}) => {
    const { url } = useGetLink(fields.linkUrl);
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const parentProductId = useContext(ParentProductIdContext);

    const currentProductId = parentProductId || productContext.product?.id;

    const safeProducts = useMemo(() => (products ? products.filter(o => !!o) : []), [products]);
    const isGlobalViewsTheme = useMemo(() => isThemeActive(THEME_IDS.GLOBAL_VIEWS), []);

    const [windowResizeTime, setWindowResizeTime] = useState(0);
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    const isMobile = useMemo(() => {
        if (typeof window === "undefined") {
            return false;
        }
        return window.innerWidth < theme.breakpoints.values[1];
    }, [windowResizeTime, theme.breakpoints.values]);

    // Match SurCustomProductCarousel mobile flow: for GV theme, prefer carousel on mobile (even when <= 3 products)
    const shouldUseCarousel = safeProducts.length > 3 || (isGlobalViewsTheme && isMobile && safeProducts.length > 1);
    const gridProducts = useMemo(() => (shouldUseCarousel ? [] : safeProducts), [shouldUseCarousel, safeProducts]);
    const styles = useMemo(
        () => getThemeAwareStyles(isGlobalViewsTheme, shouldUseCarousel ? 3 : gridProducts.length),
        [isGlobalViewsTheme, shouldUseCarousel, gridProducts.length],
    );

    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned || "",
            displayProductsFrom: fields.displayProductsFrom || "allCategories",
            selectedCategoryIds: fields.selectedCategoryIds || [],
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay || 4,
            widgetPosition: fields.widgetPosition || 1,
            isProductDetailsPage,
            productId: currentProductId,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        fields.widgetPosition,
        currentProductId,
        category?.id,
        brand?.id,
        productRecommendationWidgets,
        session?.pickUpWarehouse?.id,
        id,
    ]);

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [slidesToScroll, setSlidesToScroll] = useState(3);

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return 3;
        }

        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            return 1;
        }
        if (innerWidth < theme.breakpoints.values[2]) {
            return 2;
        }
        return 3;
    }, [theme.breakpoints.values]);

    useEffect(() => {
        if (!shouldUseCarousel) {
            return;
        }
        setSlidesToScroll(getSlidesToScroll());
    }, [shouldUseCarousel, windowResizeTime, getSlidesToScroll]);

    const setCanScroll = useCallback(() => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    }, [embla, shellContext.isInShell]);

    useEffect(() => {
        if (!shouldUseCarousel || !embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [shouldUseCarousel, embla, setCanScroll]);

    useEffect(() => {
        if (!shouldUseCarousel || !embla || safeProducts.length === 0) {
            return;
        }

        embla.reInit({
            align:
                isGlobalViewsTheme && typeof window !== "undefined" && window.innerWidth < theme.breakpoints.values[1]
                    ? "center"
                    : "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: isGlobalViewsTheme ? false : safeProducts.length > slidesToScroll,
            containScroll: "trimSnaps",
        });
        setCanScroll();
    }, [shouldUseCarousel, embla, slidesToScroll, safeProducts.length, setCanScroll, isGlobalViewsTheme, theme.breakpoints.values]);

    const slideWidth = useMemo(() => {
        if (!shouldUseCarousel) {
            return "100%";
        }

        if (isGlobalViewsTheme && isMobile) {
            return "calc(100% - 40px)";
        }

        return `calc(100% / ${slidesToScroll})`;
    }, [shouldUseCarousel, isGlobalViewsTheme, isMobile, slidesToScroll]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load related products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.outerWrapper}>
            <StyledWrapper {...styles.innerContainer}>
                {fields.title && (
                    <StyledWrapper {...styles.titleContainer}>
                        <Typography {...styles.titleText}>{fields.title}</Typography>
                    </StyledWrapper>
                )}
                
                {!shouldUseCarousel && (
                    <GridContainer {...styles.gridContainer}>
                        {gridProducts.map(product => (
                            <GridItem key={product.id} {...styles.productGridItem}>
                                <StyledWrapper {...styles.productWrapper}>
                                    <StyledWrapper {...styles.productItemWrapper}>
                                        <SurProductListProductGridCrad
                                            carouselId={id}
                                            product={product}
                                            showImage={true}
                                            showBrand={false}
                                            showTitle={true}
                                            centeredTitle={isGlobalViewsTheme}
                                            showPartNumbers={false}
                                            showPrice={false}
                                            showAddToCart={false}
                                            showAddToList={false}
                                            showGVExtraDescription={false}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                        ))}
                    </GridContainer>
                )}

                {shouldUseCarousel && (
                    <GridContainer {...styles.carouselMainContainer} data-test-selector="surRelatedProductsCarousel">
                        <GridItem {...styles.prevArrowGridItem}>
                            {!isGlobalViewsTheme && !shellContext.isInShell && safeProducts.length > slidesToScroll && (
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    aria-label="Previous"
                                    data-test-selector="prevBtn"
                                >
                                    <ButtonIcon src={ChevronLeft} />
                                </Button>
                            )}
                        </GridItem>

                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                {isGlobalViewsTheme && !shellContext.isInShell && safeProducts.length > slidesToScroll && (
                                    <>
                                        <StyledWrapper {...styles.prevArrowWrapper}>
                                            <Button
                                                {...styles.prevArrowButton}
                                                onClick={() => embla && embla.scrollPrev()}
                                                disabled={!canScrollPrev}
                                                aria-label="Previous"
                                                data-test-selector="prevBtn"
                                            >
                                                <ButtonIcon src={GVLeftArrow} />
                                            </Button>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.nextArrowWrapper}>
                                            <Button
                                                {...styles.nextArrowButton}
                                                onClick={() => embla && embla.scrollNext()}
                                                disabled={!canScrollNext}
                                                aria-label="Next"
                                                data-test-selector="nextBtn"
                                            >
                                                <ButtonIcon src={GVRightArrow} />
                                            </Button>
                                        </StyledWrapper>
                                    </>
                                )}
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {safeProducts.map(product => (
                                            <StyledWrapper
                                                {...styles.carouselSlide}
                                                key={product.id}
                                                style={{ width: slideWidth }}
                                            >
                                                <StyledWrapper {...styles.carouselSlideInner} data-test-selector="carousel_slide">
                                                    <StyledWrapper {...styles.productWrapper}>
                                                        <StyledWrapper {...styles.productItemWrapper}>
                                                            <SurProductListProductGridCrad
                                                                carouselId={id}
                                                                product={product}
                                                                showImage={true}
                                                                showBrand={false}
                                                                showTitle={true}
                                                                centeredTitle={isGlobalViewsTheme}
                                                                showPartNumbers={false}
                                                                showPrice={false}
                                                                showAddToCart={false}
                                                                showAddToList={false}
                                                                showGVExtraDescription={false}
                                                            />
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>

                        <GridItem {...styles.nextArrowGridItem}>
                            {!isGlobalViewsTheme && !shellContext.isInShell && safeProducts.length > slidesToScroll && (
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    aria-label="Next"
                                    data-test-selector="nextBtn"
                                >
                                    <ButtonIcon src={ChevronRight} />
                                </Button>
                            )}
                        </GridItem>
                    </GridContainer>
                )}
                
                {fields.linkText && url && (
                    <StyledWrapper {...styles.linkContainer}>
                        <StyledLink
                            href={url}
                            target={fields.openInNewWindow ? "_blank" : undefined}
                            rel={fields.openInNewWindow ? "noopener noreferrer" : undefined}
                            aria-label={fields.linkText}
                        >
                            {fields.linkText}
                        </StyledLink>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(RelatedProductsGrid))),
    definition: {
        group: "Basic",
        icon: "grid",
        displayName: "Sur Related Products Grid",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "crossSells", displayName: "Cross Sells" },
                    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
                    { value: "recentlyViewed", displayName: "Recently Viewed" },
                    { value: "relatedProducts", displayName: "Related Products" },
                    { value: "topSellers", displayName: "Top Sellers" },
                    { value: "recommendedProducts", displayName: "Recommended Products" },
                ],
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 1,
                customFilter: (item, _, settings) => {
                    const {
                        settingsCollection: {
                            websiteSettings: { enableDynamicRecommendations },
                            productSettings: { enableProductRecommendations },
                        },
                    } = settings;

                    if (!enableDynamicRecommendations && item.value === "topSellers") {
                        return false;
                    }

                    if (!enableProductRecommendations && item.value === "recommendedProducts") {
                        return false;
                    }

                    return true;
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 2,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 3,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 4,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number of Products to Display (Max 4)",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                    pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased",
                sortOrder: 6,
            },
            {
                name: fields.widgetPosition,
                displayName: "Widget Position",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 1,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "recommendedProducts",
                sortOrder: 7,
            },
            {
                fieldType: "Translatable",
                name: fields.linkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 8,
            },
            {
                name: fields.linkUrl,
                displayName: "Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.openInNewWindow,
                displayName: "Open Link in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 10,
            },
        ],
    },
};

export default widgetModule;
